import React from "react";
import { FiPlus, FiTrash2 } from "react-icons/fi";

import type { SurveyQuestion, SurveyQuestionOption } from "../../lib/api";
import { uniqueOptionId } from "./builderUtils";

type Props = {
  question: SurveyQuestion;
  disabled?: boolean;
  onChange: (patch: Partial<SurveyQuestion>) => void;
};

const OptionsEditor: React.FC<Props> = ({ question, disabled, onChange }) => {
  const options = question.options ?? [];

  const setOptions = (next: SurveyQuestionOption[]) =>
    onChange({ options: next });

  const updateLabel = (index: number, label: string) =>
    setOptions(options.map((o, i) => (i === index ? { ...o, label } : o)));

  const addOption = () => {
    const taken = new Set(options.map((o) => o.id));
    setOptions([
      ...options,
      { id: uniqueOptionId(`option_${options.length + 1}`, taken), label: "" },
    ]);
  };

  const removeOption = (index: number) =>
    setOptions(options.filter((_, i) => i !== index));

  return (
    <div className="space-y-2.5">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-[#5C6B76]">
        Options
      </p>

      <ul className="space-y-2">
        {options.map((option, i) => (
          <li key={option.id} className="flex items-center gap-2">
            <span
              className={`h-3.5 w-3.5 shrink-0 border border-[#3A4852] ${
                question.type === "MULTI_CHOICE" ? "rounded-[3px]" : "rounded-full"
              }`}
              aria-hidden
            />
            <input
              type="text"
              value={option.label}
              disabled={disabled}
              onChange={(e) => updateLabel(i, e.target.value)}
              placeholder={`Option ${i + 1}`}
              className="h-10 min-w-0 flex-1 rounded-lg border border-[#2C363E] bg-[#13181B] px-3 text-[13px] text-[#E7EBEE] outline-none transition placeholder:text-[#5C6B76] focus:border-secondary_button disabled:opacity-60"
            />
            <button
              type="button"
              disabled={disabled || options.length <= 2}
              onClick={() => removeOption(i)}
              aria-label={`Remove option ${i + 1}`}
              className="shrink-0 rounded-lg border border-[#2C363E] p-2 text-[#5C6B76] transition hover:border-red-500/40 hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <FiTrash2 className="text-sm" />
            </button>
          </li>
        ))}
      </ul>

      <button
        type="button"
        disabled={disabled}
        onClick={addOption}
        className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-secondary_button transition hover:brightness-110 disabled:opacity-40"
      >
        <FiPlus className="text-sm" />
        Add option
      </button>

      <label className="mt-1 flex cursor-pointer items-start gap-2.5 rounded-lg border border-[#2C363E] bg-[#13181B] px-3 py-2.5">
        <input
          type="checkbox"
          checked={Boolean(question.allowOther)}
          disabled={disabled}
          onChange={(e) => onChange({ allowOther: e.target.checked })}
          className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-secondary_button"
        />
        <span className="min-w-0">
          <span className="block text-[13px] font-medium text-[#E7EBEE]">
            Allow “Other” with free text
          </span>
          <span className="mt-0.5 block text-[11px] leading-relaxed text-[#869AA9]">
            Adds an “Other” choice. Picking it opens a text box so you learn what the
            option list is missing.
          </span>
        </span>
      </label>

      {question.allowOther && (
        <input
          type="text"
          value={question.otherPlaceholder ?? ""}
          disabled={disabled}
          onChange={(e) => onChange({ otherPlaceholder: e.target.value })}
          placeholder="Placeholder for the “Other” box"
          className="h-10 w-full rounded-lg border border-[#2C363E] bg-[#13181B] px-3 text-[13px] text-[#E7EBEE] outline-none transition placeholder:text-[#5C6B76] focus:border-secondary_button disabled:opacity-60"
        />
      )}
    </div>
  );
};

export default OptionsEditor;
